import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getAllUsers,
  getSupervisors,
  updateUserSupervision,
  getAvailableTeams,
  getUserData
} from '../services/firebaseService';
import { User } from '../types';
import './AdminPage.css';

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editingSupervision, setEditingSupervision] = useState<string[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [accessChecked, setAccessChecked] = useState(false);
  const availableTeams = getAvailableTeams();

  const checkUserAccess = async () => {
    if (!user) return;
    
    try {
      const data = await getUserData(user.uid);
      console.log('Admin page - User data loaded:', data);
      setUserData(data);
      
      if (data.type === 'admin') {
        loadSupervisors();
      } else {
        // Redirect non-admin users
        alert('Acesso negado. Esta página é restrita a administradores.');
        window.close();
      }
    } catch (error) {
      console.error('Error loading user data for admin check:', error);
      alert('Erro ao verificar permissões de usuário.');
      window.close();
    } finally {
      setAccessChecked(true);
    }
  };

  useEffect(() => {
    if (user) {
      checkUserAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSupervisors = async () => {
    setLoading(true);
    try {
      console.log('Loading all users to check supervisor data...');
      const allUsers = await getAllUsers();
      console.log('All users loaded:', allUsers);
      console.log('User types found:', allUsers.map(u => ({ name: u.name, type: u.type })));
      
      const supervisorData = await getSupervisors();
      console.log('Filtered supervisors:', supervisorData);
      setSupervisors(supervisorData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading supervisors:', error);
      alert('Erro ao carregar supervisores.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userId: string, currentSupervision: string[]) => {
    setEditingUser(userId);
    setEditingSupervision(currentSupervision || []);
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditingSupervision([]);
  };

  const handleSave = async (userId: string) => {
    setSaving(userId);
    try {
      await updateUserSupervision(userId, editingSupervision);
      
      // Update local state
      setSupervisors(prev => 
        prev.map(supervisor => 
          supervisor.uid === userId 
            ? { ...supervisor, supervision: editingSupervision }
            : supervisor
        )
      );
      
      setEditingUser(null);
      setEditingSupervision([]);
    } catch (error) {
      console.error('Error updating supervisor:', error);
      alert('Erro ao atualizar supervisor.');
    } finally {
      setSaving(null);
    }
  };

  const handleTeamToggle = (teamId: string) => {
    if (editingSupervision.includes(teamId)) {
      // Remove team
      setEditingSupervision(prev => prev.filter(t => t !== teamId));
    } else {
      // Add team
      setEditingSupervision(prev => [...prev, teamId]);
    }
  };

  const parseSupervisionTeams = (supervision?: string[]): string[] => {
    if (!supervision) return [];
    return supervision.filter(team => team && team !== 'N/A');
  };

  if (!accessChecked) {
    return (
      <div className="admin-page-container">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          Verificando permissões...
        </div>
      </div>
    );
  }

  if (userData?.type !== 'admin') {
    return (
      <div className="admin-page-container">
        <div className="access-denied">
          <h1>Acesso Negado</h1>
          <p>Esta página é restrita a administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="admin-header">
        <div className="header-content">
          <h1>Administração de Usuários</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-section">
          <h2>Gerenciar Supervisores</h2>
          <p>Configure quais equipes cada supervisor pode acessar.</p>

          {loading ? (
            <div className="loading">
              <i className="fas fa-spinner fa-spin"></i>
              Carregando supervisores...
            </div>
          ) : (
            <div className="supervisors-table-wrapper">
              <table className="supervisors-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Equipes Supervisionadas</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {supervisors.map((supervisor) => (
                    <tr key={supervisor.uid}>
                      <td className="name-cell">
                        <strong>{supervisor.name}</strong>
                      </td>
                      <td className="email-cell">{supervisor.email}</td>
                      <td className="teams-cell">
                        {editingUser === supervisor.uid ? (
                          <div className="teams-editor">
                            <div className="teams-grid">
                              {availableTeams.map((team) => (
                                <label key={team} className="team-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={editingSupervision.includes(team)}
                                    onChange={() => handleTeamToggle(team)}
                                  />
                                  <span className="team-name">{team}</span>
                                </label>
                              ))}
                            </div>
                            <div className="teams-summary">
                              <strong>Selecionadas:</strong> {editingSupervision.join(', ') || 'Nenhuma'}
                            </div>
                          </div>
                        ) : (
                          <div className="teams-display">
                            {parseSupervisionTeams(supervisor.supervision).length > 0 ? (
                              <div className="team-badges">
                                {parseSupervisionTeams(supervisor.supervision).map((team) => (
                                  <span key={team} className="team-badge">{team}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="no-teams">Nenhuma equipe atribuída</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="actions-cell">
                        {editingUser === supervisor.uid ? (
                          <div className="action-buttons">
                            <button
                              onClick={() => handleSave(supervisor.uid)}
                              disabled={saving === supervisor.uid}
                              className="save-btn"
                            >
                              {saving === supervisor.uid ? (
                                <>
                                  <i className="fas fa-spinner fa-spin"></i>
                                  Salvando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-check"></i>
                                  Salvar
                                </>
                              )}
                            </button>
                            <button onClick={handleCancel} className="cancel-btn">
                              <i className="fas fa-times"></i>
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(supervisor.uid, supervisor.supervision || [])}
                            className="edit-btn"
                          >
                            <i className="fas fa-edit"></i>
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {supervisors.length === 0 && !loading && (
                <div className="no-data">
                  <i className="fas fa-users"></i>
                  <h3>Nenhum supervisor encontrado</h3>
                  <p>Não foram encontrados usuários com perfil de supervisor no sistema.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;